import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { format, parseISO } from 'date-fns';
import React, { Fragment, useMemo, useState } from 'react';
import { useAppSelector } from '../../../../redux/hooks';
import { selectCards, selectDividers, selectLists } from '../../../../redux/trip/trip.selectors';
import { TripDay, TripEvent, TripID } from '../../../../redux/trip/trip.types';
import { ItemType } from '../../../../types/DragAndDrop';
import { getOpeningHours } from '../../../../utils/Card';
import { getEmojiAndColorForCard } from '../../../../utils/getEmojiAndColorForCard';
import ConfirmDialog from '../../../ConfirmDialog/ConfirmDialog';
import type { PendingDrop } from '../../DnD/types';
import SortableItineraryEvent from './SortableItineraryEvent';
import './ItineraryEvents.scss';

interface ItineraryEventsProps {
  tripId: TripID;
  tripDay: TripDay;
  events?: TripEvent[];
  col: number;
  canEdit?: boolean;
  // eventId → marker number, present only in day view
  markerNumbers?: Record<string, number>;
  pendingDrop?: PendingDrop | null;
  onConfirmDrop?: () => void;
  onCancelDrop?: () => void;
}

function ItineraryEvents({
  tripId,
  tripDay,
  events,
  col,
  canEdit,
  markerNumbers,
  pendingDrop,
  onConfirmDrop,
  onCancelDrop,
}: ItineraryEventsProps) {
  const cards = useAppSelector(selectCards);
  const lists = useAppSelector(selectLists);
  const dividers = useAppSelector(selectDividers);
  const eventOrder = tripDay?.eventOrder;

  // Track whether a draggable card or event is active so we can show/hide highlights
  const [isCompatibleDragActive, setIsCompatibleDragActive] = useState(false);
  useDndMonitor({
    onDragStart: ({ active }) => {
      const type = active.data.current?.type as string | undefined;
      setIsCompatibleDragActive(type === ItemType.CARD || type === ItemType.EVENT);
    },
    onDragEnd: () => setIsCompatibleDragActive(false),
    onDragCancel: () => setIsCompatibleDragActive(false),
  });

  const { setNodeRef, isOver } = useDroppable({
    id: `day-${tripDay?.dateISO ?? 'unknown'}`,
    data: { type: 'day', dateISO: tripDay?.dateISO, tripDay },
  });

  // Only include single-day event IDs in SortableContext — multi-day events are rendered
  // in a separate row and must not be counted as sortable targets here.
  const sortableEventIds = useMemo(
    () =>
      (events ?? [])
        .filter((e) => !e.eventLength)
        .map((e) => e.id)
        .filter((id): id is string => id !== undefined),
    [events]
  );

  const showDuplicate =
    pendingDrop?.type === 'duplicate' &&
    pendingDrop.dateISO === tripDay?.dateISO &&
    onConfirmDrop !== undefined &&
    onCancelDrop !== undefined;
  const showClosed =
    pendingDrop?.type === 'closed' &&
    pendingDrop.dateISO === tripDay?.dateISO &&
    onConfirmDrop !== undefined &&
    onCancelDrop !== undefined;

  const containerClass = [
    'itinerary-events-container',
    isCompatibleDragActive && isOver ? 'is-over' : '',
    isCompatibleDragActive && !isOver ? 'can-drop' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div
        ref={setNodeRef}
        className={containerClass}
        style={{ gridColumn: `${col} / span 1` }}
      >
        {cards && lists && (
          <SortableContext
            items={sortableEventIds}
            strategy={verticalListSortingStrategy}
          >
            {events
              ?.filter((event) => !event.eventLength && cards[event.cardId])
              .map((event) => {
                const { emoji, color, listLabel } = getEmojiAndColorForCard(
                  event.cardId,
                  lists,
                  dividers ?? []
                );
                return (
                  <SortableItineraryEvent
                    key={event.id}
                    tripId={tripId}
                    event={event}
                    card={cards[event.cardId]}
                    emoji={emoji}
                    color={color}
                    listLabel={listLabel}
                    eventOrder={eventOrder}
                    canEdit={canEdit}
                    markerNumber={markerNumbers && event.id ? markerNumbers[event.id] : undefined}
                  />
                );
              })}
          </SortableContext>
        )}
      </div>

      {showDuplicate && pendingDrop && (
        <ConfirmDialog
          open
          title='Add again?'
          confirmLabel='Add'
          cancelLabel='Skip'
          onCancel={onCancelDrop}
          onConfirm={onConfirmDrop}
        >
          <span className='location-name'>
            {pendingDrop.card.customName ?? pendingDrop.card.name}
          </span>{' '}
          is already on your <strong>{format(parseISO(pendingDrop.dateISO), 'EEEE, MMM d')}</strong>{' '}
          itinerary.
        </ConfirmDialog>
      )}

      {showClosed && pendingDrop && (
        <ConfirmDialog
          open
          title='Heads up!'
          confirmLabel={pendingDrop.event ? 'Move anyway' : 'Add anyway'}
          cancelLabel='Skip'
          onCancel={onCancelDrop}
          onConfirm={onConfirmDrop}
        >
          <span className='location-name'>
            {pendingDrop.card.customName ?? pendingDrop.card.name}
          </span>{' '}
          appears to be closed on {format(parseISO(pendingDrop.dateISO), 'EEEE')}s.
          {pendingDrop.card.openingHours ? (
            <>
              {' Their hours are:'}
              <div className='closed-warning-dialog-hours-container'>
                {getOpeningHours(pendingDrop.card.openingHours).map((openingHours, dayIndex) => {
                  const dayOfWeek = Object.keys(openingHours)[0];
                  const dayHours = openingHours[dayOfWeek];
                  const isClosedDay = dayHours.some((hours) =>
                    hours.toLowerCase().includes('closed')
                  );
                  const row = dayIndex + 1;
                  return (
                    <Fragment key={`${pendingDrop.card.id}-${dayOfWeek}`}>
                      <span
                        className='closed-warning-dialog-day-container'
                        style={{ gridRow: row }}
                      >
                        <span
                          className={`highlighted-text${isClosedDay ? ' highlighted-text--highlighted' : ''}`}
                        >
                          {dayOfWeek}:
                        </span>
                      </span>
                      <div
                        className='closed-warning-dialog-time-container'
                        style={{ gridRow: row }}
                      >
                        {openingHours[dayOfWeek].map((hours, index) => (
                          <span
                            className={`highlighted-text${isClosedDay ? ' highlighted-text--highlighted' : ''}`}
                            key={`${pendingDrop.card.id}-hours-${dayOfWeek}-${index}`}
                          >
                            {hours}
                          </span>
                        ))}
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </>
          ) : null}
          {' Do you still want to '}
          {pendingDrop.event ? 'move' : 'add'} it?
        </ConfirmDialog>
      )}
    </>
  );
}

ItineraryEvents.displayName = 'ItineraryEvents';
export default React.memo(ItineraryEvents);
